import React, { useCallback, useState } from 'react';
import { Paragraph4 } from '@nexus/lib-react/dist/core/Typography';
import S from './style/Dac7DateField';
import V, { Validity } from '../utils/validation';

import { SellerPrivateDac7, SellerSoleTraderDac7 } from '../../../../../../typings/dac7';
import { useI18n } from '../../../../../i18n';
import useDate from './useDac7Date';
import { PathWithDot } from '../types/Dac7FormInputName';

interface Dac7DateFieldInnerProps {
  value: string;
  label: string;
  name: `dac7_${PathWithDot<SellerPrivateDac7['data'] | SellerSoleTraderDac7['data']>}`;
  required: boolean;
  disabled: boolean;
  error?: string;
  onChange?: (value: string) => void;
  onBlur?: (event: React.SyntheticEvent) => void;
}

const Dac7DateFieldInner = ({
  value,
  label,
  name,
  required,
  disabled,
  error,
  onChange,
  onBlur,
}: Dac7DateFieldInnerProps) => {
  const i18n = useI18n();

  const [isTouched, setIsTouched] = useState(false);
  const [validity, setValidity] = useState<Validity>({
    valid: true,
    error,
  });
  const showValidation = !validity.valid && isTouched;

  const handleBlur = (e: React.SyntheticEvent, currentValidity: Validity) => {
    if (onBlur) onBlur(e);
    setValidity(currentValidity);
    setIsTouched(true);
  };

  const handleChange = (currentValue: string, currentValidity: Validity) => {
    if (onChange) onChange(currentValue);
    setValidity(currentValidity);
    setIsTouched(true);
  };

  const validate = useCallback(
    currValInit => {
      const currVal = currValInit.replace(/\s/g, '');

      if (required && currVal === '') {
        const requiredMessage = `${i18n('dlv_dac7_validation_required')}`;
        return { valid: false, error: requiredMessage };
      }

      if (!new RegExp(V.DOB_PATTERN).test(currVal)) {
        const patternMessage = `${i18n('dlv_dac7_birthDate_error_is_invalid')}`;
        return { valid: false, error: patternMessage };
      }

      return V.birthDateValidations.reduce(
        (acc, validation) => {
          if (acc.valid) {
            return validation(currVal);
          }
          return acc;
        },
        { valid: true, error: '' },
      );
    },
    [V.birthDateValidations, V.DOB_PATTERN, required],
  );

  const { day, month, year, changeHandler, blurHandler } = useDate({
    dateInput: value,
    validate,
    onBlur: handleBlur,
    onChange: handleChange,
  });

  return (
    <S.DateFieldContainer className="dac7_dateField">
      <Paragraph4 mb="6" className="dac7-label">
        {label}
        {required && '*'}
      </Paragraph4>

      <input name={name} type="hidden" value={value} />

      <S.DateInnerFieldsContainer>
        <S.FieldContainer>
          <S.StyledTextField
            name="day"
            placeholder="DD"
            status={showValidation ? 'invalid' : 'default'}
            value={day}
            type="text"
            disabled={disabled}
            onChange={changeHandler}
            onBlur={blurHandler}
            renderSuffix={() => null}
          />
        </S.FieldContainer>
        <S.InnerDivider />
        <S.FieldContainer>
          <S.StyledTextField
            name="month"
            placeholder="MM"
            status={showValidation ? 'invalid' : 'default'}
            value={month}
            disabled={disabled}
            onChange={changeHandler}
            onBlur={blurHandler}
            renderSuffix={() => null}
          />
        </S.FieldContainer>
        <S.InnerDivider />
        <S.FieldContainer>
          <S.StyledTextField
            name="year"
            placeholder="YYYY"
            status={showValidation ? 'invalid' : 'default'}
            value={year}
            disabled={disabled}
            onChange={changeHandler}
            onBlur={blurHandler}
            renderSuffix={() => null}
          />
        </S.FieldContainer>
      </S.DateInnerFieldsContainer>
      {showValidation && (
        <Paragraph4 m="6px 0" color="error">
          {i18n(validity.error)}
        </Paragraph4>
      )}
    </S.DateFieldContainer>
  );
};

interface Dac7DateFieldProps {
  value: string;
  label: string;
  name: `dac7_${PathWithDot<SellerPrivateDac7['data'] | SellerSoleTraderDac7['data']>}`;
  required: boolean;
  disabled: boolean;
}

const Dac7DateField = ({ value, ...rest }: Dac7DateFieldProps) => {
  const [date, setDate] = useState(value);

  const dateChanged = (currentDateValue: string): void => {
    setDate(currentDateValue);
  };

  return <Dac7DateFieldInner value={date} onChange={dateChanged} {...rest} />;
};

export default Dac7DateField;
